/**
 * Post-checkout Welcome path fixtures.
 *
 * V1 ships with a curated greeting + 30-day path + Lab-subscription clarity
 * panel. Once the shared-context schema lock lands (see audit Fix 2), the
 * greeting + path will be hydrated from the accepted Kickstart blueprint
 * + accepted build brief instead of fixture data.
 *
 * Provenance discipline matches productHomeFixtures.ts so the surface can
 * always say where its data came from.
 *
 * COPY RULE (docs/content/shared/customer-facing-voice-rule.md): every string
 * in this file is read by a member. Second person, plain words, no internal
 * vocabulary, and nothing claimed that we cannot stand behind. Where the
 * earlier draft named specifics we can't verify (a weekly call, named internal
 * tools), the copy generalises rather than invents.
 */

import type { Provenance } from './productHomeFixtures';

// ── Greeting ───────────────────────────────────────────────────────────

export interface WelcomeGreeting {
  builderName: string;
  productName: string;
  /** Subhead under the headline. One short sentence. */
  subhead: string;
}

export const WELCOME_GREETING: WelcomeGreeting = {
  builderName: 'Pamela',
  productName: 'AI Acquisition OS',
  subhead:
    "You're in. Here's what you just unlocked, what the next 30 days look like, and the one thing to do first.",
};

// ── What you just bought ────────────────────────────────────────────────

export interface WelcomeEntitlement {
  bib: string;
  title: string;
  body: string;
  /** Short pill (e.g. "Lifetime", "30 days included", "Active"). */
  state: string;
  stateTone: 'race' | 'teal' | 'ink';
}

export const WELCOME_ENTITLEMENTS: WelcomeEntitlement[] = [
  {
    bib: 'V1',
    title: 'AI Acquisition OS',
    body: 'Your own workspace on your computer, the build to work through step by step, and the checks that catch mistakes before they cost you anything.',
    state: 'Active',
    stateTone: 'race',
  },
  {
    bib: 'L',
    title: 'The Lab — month one',
    body: "Somewhere to go when you're stuck: send Tom the question, and see what other members have already worked out. Included for your first 30 days.",
    state: 'Included',
    stateTone: 'teal',
  },
  {
    bib: 'K',
    title: 'Kickstart blueprint',
    body: "The plan you put together in Kickstart comes with you. Your AI Co-Founder reads it first, so you're not starting from a blank page.",
    state: 'Brought over',
    stateTone: 'ink',
  },
];

// ── 30-day path ─────────────────────────────────────────────────────────

export interface PathPhase {
  bib: string;
  bibTone: 'race' | 'teal' | 'ink';
  window: string;
  title: string;
  body: string;
  beats: string[];
}

export const PATH_PHASES: PathPhase[] = [
  {
    bib: '01',
    bibTone: 'race',
    window: 'Days 1 – 7',
    title: 'Set the workspace up',
    body: 'Get AI Acquisition OS onto your computer, point it at your business, and check that what it knows about you is right.',
    beats: [
      'Read back what your AI Co-Founder knows about your business, and correct anything that looks off',
      'Put the AI Acquisition OS workspace files in the folder you keep your business in',
      'Let your AI Co-Founder suggest the way forward from what you gave it',
    ],
  },
  {
    bib: '02',
    bibTone: 'teal',
    window: 'Days 8 – 21',
    title: 'Build the first version',
    body: "Turn the plan into something real: the page people land on, the way they get in touch, and the checks that it all works.",
    beats: [
      'Pick a look you like and let it build the pages for you',
      'Work out where your buyers already are, and what to say to them',
      'Get the first few ads or posts drafted and ready to go',
    ],
  },
  {
    bib: '03',
    bibTone: 'ink',
    window: 'Days 22 – 30',
    title: 'Get the first test ready',
    body: 'Check the plumbing works, set the first test up with a spending limit you choose, then read what comes back and pick the next one.',
    beats: [
      'Make sure payments, email and tracking are all working',
      'Set the campaign up paused, with your own daily limit, so nothing spends until you say so',
      'Read what the first results tell you, and decide what to change next',
    ],
  },
];

// ── First move ─────────────────────────────────────────────────────────

export interface FirstMove {
  eyebrow: string;
  headline: string;
  body: string;
  cta: { label: string; route: string };
  /** Approximate time-to-complete on the first sitting. */
  timebox: string;
}

export const FIRST_MOVE: FirstMove = {
  eyebrow: 'Today',
  headline: 'Make a start on the build.',
  body: "Your AI Co-Founder picks up what you set out in Kickstart and walks you through the first few decisions. About half an hour the first time you sit down with it.",
  cta: { label: 'Make a start', route: 'build-room' },
  timebox: '~30 min',
};

// ── Lab subscription clarity ───────────────────────────────────────────

export interface LabSubscriptionClarity {
  /** The MarkerHighlighted line — the one promise to make crystal clear. */
  highlightedLine: string;
  body: string;
  renewalNote: string;
}

export const LAB_SUBSCRIPTION_CLARITY: LabSubscriptionClarity = {
  highlightedLine: 'Month 1 of The Lab is on us.',
  body: "From day 31 it's $100 a month. You can cancel any time in Settings, and your workspace and everything you've built stay yours either way.",
  renewalNote: 'Renews · day 31 · $100 / month · cancel any time',
};

// ── If you get stuck ───────────────────────────────────────────────────

export interface StuckStrip {
  body: string;
  cta: { label: string; route: string };
}

export const STUCK_STRIP: StuckStrip = {
  body: "Stuck on something? Send it over with whatever detail you can share, and we'll pick it up from there.",
  cta: { label: 'Open The Lab', route: 'lab' },
};

// ── Provenance ─────────────────────────────────────────────────────────

/**
 * `note` is rendered on screen next to the skip link, so it is member-facing
 * copy, not a provenance stamp. The stamp itself stays in `source`.
 */
export const WELCOME_PROVENANCE: { source: Provenance; note: string } = {
  source: 'local-fixture',
  note: 'you can come back to this',
};
